// Agent loop for the Shroud landing chat.
//
// The loop drives a single user turn:
//   1. Send the running message array (system + recent history + user) to
//      CocoonClient.inference() over the encrypted WebSocket. Pass `tools`,
//      `tool_choice: "auto"`, and `chat_template_kwargs.enable_thinking: true`
//      via the cast helpers in openai-tools.ts (the SDK does not yet expose
//      these fields publicly).
//   2. Stream the response, surfacing content to the UI as tokens arrive.
//      Tool-call structure is read from `chunk.toolCalls` (passthrough field
//      on the SDK Chunk; populated by the cocoon-client polyfill from any
//      `<tool_call>` block the worker emits).
//   3. After the stream completes, if `roundToolCalls` is non-empty, push
//      the assistant turn (with `tool_calls`) and then execute each call
//      through /rpc (anonymous, CapRead), pushing each result back as a
//      `{role:'tool', tool_call_id, content}` message. Loop.
//   4. Stop on the first round that produces no tool calls or when we hit
//      MAX_TOOL_ROUNDS (DoS guard).

import { CocoonClient } from '@alphatoncapital/shroud-sdk';
import type {
  Message,
  StoredMessage,
  ToolCallTrace,
  DocsSearchResult,
  OpenaiTool,
  OpenaiToolCall,
} from './types.js';
import { MAX_TOOL_ROUNDS, LOW_CONFIDENCE_THRESHOLD } from './config.js';
import { executeToolCall, assessConfidence } from './tools.js';
import { buildDocsContext } from './system-prompt.js';
import {
  asInferenceRequest,
  asOpenaiChunk,
  asOpenaiToolMessage,
  asAssistantToolCallsMessage,
} from './openai-tools.js';
import { dbg, dbgErr, preview } from './debug.js';

/** Callbacks for the UI to observe orchestrator state changes. */
export interface OrchestratorCallbacks {
  onStreamStart: () => void;
  onStreamChunk: (content: string, fullContent: string) => void;
  onStreamEnd: (fullContent: string) => void;
  onToolCallStart: (name: string) => void;
  onToolCallEnd: (trace: ToolCallTrace) => void;
  onError: (message: string) => void;
  onLowConfidence: () => void;
}

/**
 * Run the agent loop for a single user message and return the final
 * assistant message (with all tool traces attached).
 */
export async function runAgentLoop(
  client: CocoonClient,
  model: string,
  systemPrompt: string,
  history: StoredMessage[],
  userMessage: string,
  openaiTools: OpenaiTool[],
  callbacks: OrchestratorCallbacks,
): Promise<StoredMessage> {
  const messages: Message[] = [
    { role: 'system', content: systemPrompt },
    ...history
      .filter((m) => m.role !== 'system')
      .slice(-10) // Keep the last 10 messages of context.
      .map((m) => ({ role: m.role, content: m.content })),
    { role: 'user', content: userMessage },
  ];

  const allToolTraces: ToolCallTrace[] = [];
  let finalContent = '';
  let isLowConfidence = false;
  let round = 0;

  dbg('orchestrator: enter', {
    model,
    messageCount: messages.length,
    roles: messages.map((m) => m.role),
    systemPromptLen: systemPrompt.length,
    userMessage,
    openaiToolCount: openaiTools.length,
    openaiToolNames: openaiTools.map((t) => t.function.name),
  });

  while (round < MAX_TOOL_ROUNDS) {
    dbg(`orchestrator: round ${round} starting inference`, {
      model,
      messageCount: messages.length,
      lastRole: messages[messages.length - 1]?.role,
      sendingTools: openaiTools.length > 0,
    });
    callbacks.onStreamStart();

    let fullResponse = '';
    let roundToolCalls: OpenaiToolCall[] = [];
    try {
      const inferenceStart = performance.now();
      const stream =
        openaiTools.length > 0
          ? await client.inference(
              asInferenceRequest({
                model,
                messages,
                stream: true,
                tools: openaiTools,
                tool_choice: 'auto',
                chat_template_kwargs: { enable_thinking: true },
              }),
            )
          : await client.inference({
              model,
              messages,
              stream: true,
            });
      dbg(`orchestrator: round ${round} stream opened`, {
        elapsedMs: Math.round(performance.now() - inferenceStart),
        sentTools: openaiTools.length > 0,
      });

      let chunkCount = 0;
      let toolCallChunkCount = 0;
      for await (const chunk of stream) {
        chunkCount++;
        const view = asOpenaiChunk(chunk);
        if (view.content) {
          fullResponse += view.content;
          callbacks.onStreamChunk(view.content, fullResponse);
        }
        const tc = view.toolCalls;
        if (tc && Array.isArray(tc) && tc.length > 0) {
          toolCallChunkCount++;
          // Single-call MVP: last-event-wins assignment. The polyfill emits
          // the structured tool_calls array once per round.
          roundToolCalls = tc as OpenaiToolCall[];
        }
      }
      dbg(`orchestrator: round ${round} stream finished`, {
        chunkCount,
        toolCallChunkCount,
        fullLen: fullResponse.length,
        fullPreview: preview(fullResponse),
        roundToolCallCount: roundToolCalls.length,
        roundToolCalls: roundToolCalls.map((t) => ({
          id: t.id,
          name: t.function.name,
          args: t.function.arguments,
        })),
        elapsedMs: Math.round(performance.now() - inferenceStart),
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Inference failed';
      dbgErr(`orchestrator: round ${round} inference threw`, err);
      callbacks.onError(errorMsg);
      return {
        role: 'assistant',
        content: 'Could not reach Shroud. Check your connection and send again.',
        toolCalls: allToolTraces.length > 0 ? allToolTraces : undefined,
        lowConfidence: false,
      };
    }

    callbacks.onStreamEnd(fullResponse);

    if (roundToolCalls.length === 0) {
      // No tool calls — this is the final assistant turn.
      finalContent = fullResponse;
      dbg(`orchestrator: round ${round} no tool calls -> finalize`, {
        finalLen: finalContent.length,
        finalPreview: preview(finalContent),
      });
      break;
    }

    // Tool calls present. Push the assistant turn with `tool_calls` so the
    // round-1 polyfill can rewrite the prior turn into the model's native
    // canonical shape, then execute each tool and feed the result back.
    messages.push(asAssistantToolCallsMessage(fullResponse, roundToolCalls));
    dbg(`orchestrator: round ${round} pushed assistant tool_calls turn`, {
      messageCountAfter: messages.length,
      toolCallCount: roundToolCalls.length,
    });

    for (const call of roundToolCalls) {
      dbg(`orchestrator: round ${round} executing tool`, {
        id: call.id,
        name: call.function.name,
        rawArgs: call.function.arguments,
      });
      callbacks.onToolCallStart(call.function.name);

      let parsedArgs: Record<string, unknown> = {};
      let parseError: string | undefined;
      try {
        // The polyfill emits arguments as a JSON string per OpenAI canonical.
        const parsed = JSON.parse(call.function.arguments);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedArgs = parsed as Record<string, unknown>;
        } else {
          parseError = 'tool_call arguments did not decode to an object';
        }
      } catch (e) {
        parseError = e instanceof Error ? e.message : 'invalid tool_call arguments JSON';
      }

      if (parseError) {
        dbgErr(`orchestrator: round ${round} arg parse error`, {
          name: call.function.name,
          rawArgs: call.function.arguments,
          parseError,
        });
      }

      const result = parseError
        ? { content: null, elapsedMs: 0, error: parseError }
        : await executeToolCall(call.function.name, parsedArgs);
      dbg(`orchestrator: round ${round} tool result`, {
        id: call.id,
        name: call.function.name,
        elapsedMs: Math.round(result.elapsedMs),
        error: result.error,
        contentType: Array.isArray(result.content)
          ? `array[${result.content.length}]`
          : typeof result.content,
      });

      const trace: ToolCallTrace = {
        id: call.id,
        name: call.function.name,
        args: parsedArgs,
        result: result.content,
        error: result.error,
        elapsedMs: Math.round(result.elapsedMs),
      };
      allToolTraces.push(trace);
      callbacks.onToolCallEnd(trace);

      if (call.function.name === 'docs_search' && !result.error) {
        const results = (result.content as DocsSearchResult[]) ?? [];
        const confidence = assessConfidence(results, LOW_CONFIDENCE_THRESHOLD);
        const query = (parsedArgs.query as string) ?? '';
        dbg(`orchestrator: docs_search confidence`, {
          query,
          resultCount: results.length,
          topScore: results[0]?.score,
          confidence,
        });
        const contextMsg = buildDocsContext(query, results, confidence);
        if (confidence === 'low' || confidence === 'none') {
          isLowConfidence = true;
          callbacks.onLowConfidence();
        }
        messages.push(asOpenaiToolMessage(call.id, contextMsg));
      } else {
        const resultStr = result.error
          ? `Error: ${result.error}`
          : JSON.stringify(result.content);
        messages.push(asOpenaiToolMessage(call.id, resultStr));
      }
    }

    round++;
  }

  if (round >= MAX_TOOL_ROUNDS && !finalContent) {
    dbgErr('orchestrator: hit MAX_TOOL_ROUNDS without final content', {
      round,
      MAX_TOOL_ROUNDS,
    });
    finalContent =
      'Tool call limit reached for this message. Rephrase your question to try a different approach.';
  }

  dbg('orchestrator: exit', {
    round,
    finalLen: finalContent.length,
    finalPreview: preview(finalContent),
    toolTraceCount: allToolTraces.length,
    isLowConfidence,
  });

  return {
    role: 'assistant',
    content: finalContent,
    toolCalls: allToolTraces.length > 0 ? allToolTraces : undefined,
    lowConfidence: isLowConfidence || undefined,
  };
}
